<template>
  <div v-show="show" class="member-place">
    <!-- 検索ツールバー -->
    <div class="tool-bar">
      <member-place-filter v-on:condition-changed="filterCondition = $event" />
    </div>
    <!-- タイトルバー -->
    <div class="title-bar">
      <h1>{{ $tc('appMemberPlace.places', placeCount) }}</h1>
      <div class="selector">
        <member-place-option-menu
          v-bind:additionalItems="additionalItems"
          v-bind:is-simple="isSimple"
          v-on:items-changed="$_onItemsChanged"
          v-on:is-simple-changed="$_onIsSimpleChanged"
        />
      </div>
    </div>
    <!-- リスト -->
    <div class="list-wrapper">
      <loading-mask v-bind:is-loading="isLoading">
        <recycle-scroller
          ref="place-list-inner"
          class="list-inner"
          v-bind:items="placesForCard"
          v-bind:item-size="cardHeight"
          v-slot="{ item }"
        >
          <card-member-place
            class="place-item"
            v-bind="item"
            v-on:placecard-click="$_onClickCardBody"
            v-on:placecard-title-click="$_onClickCardTitle"
          />
        </recycle-scroller>
        <!-- ページネーション -->
        <div class="list-footer">
          <b-pagination
            ref="pagination"
            v-model="currentPage"
            v-bind:total-rows="placeCount"
            v-bind:per-page="limit"
            align="center"
            size="sm"
            class="place-pagination"
          />
        </div>
      </loading-mask>
    </div>
    <!-- カード高さ計測用隠し要素 -->
    <div class="hidden">
      <card-member-place
        v-if="placesForCard.length"
        ref="card"
        class="place-item"
        v-bind="placesForCard[0]"
      />
    </div>
  </div>
</template>

<script>
import MemberPlaceFilter from '@/components/member/AppMemberPlaceFilter.vue'
import MemberPlaceOptionMenu from '@/components/member/AppMemberPlaceOptionMenu.vue'
import CardMemberPlace from '@/components/member/AppCardMemberPlace.vue'
import Bounds from '@/model/geo/Bounds.js'
import { NULL_STRING_API } from '@/constants.js'
import { RestApiClient } from 'ifield-core-components'
import { toServerTz } from '@/js/TimezoneHelper'
import moment from 'moment'
import _ from 'lodash'
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { usePlaceAdditionalItems } from '@/composables/place/UsePlaceAdditionalItems.js'

/**
 * momentオブジェクト化(invalid時はnullを返す)
 * @param {String|Date|Moment} value moment化する値
 * @param {Array} args 残余引数(momentのコンストラクタにそのまま流す)
 * @returns {Moment}
 */
const toMoment = (value, ...args) => {
  if (!value) return null
  if (moment.isMoment(value)) return value
  const momentValue = moment(value, ...args)
  return momentValue.isValid() ? momentValue : null
}

/**
 * カード高さの初期値(px)
 *
 * 実際の高さは描画後に計測して差し替える。任意項目の有無で変わるため
 * @type {Number}
 */
const INITIAL_CARD_HEIGHT = 136

/**
 * 地図画面 - プレイスタブ
 */
export default {
  name: 'AppMemberPlace',
  components: {
    MemberPlaceFilter,
    MemberPlaceOptionMenu,
    RecycleScroller,
    CardMemberPlace,
  },
  props: {
    /**
     * 表示フラグ
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * 1ページあたりの表示件数
     */
    limit: {
      type: Number,
      default: 10,
    },
    /**
     * 地図の表示bounds
     */
    bounds: {
      type: Object,
      default: null,
    },
    /**
     * 絞り込み条件
     */
    condition: {
      type: Object,
      default: () => {},
    },
    /**
     * プレイスが1つの時に地図をfitする場合はtrue
     */
    canFitMapToAlone: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    const { isSimple, items: additionalItems } = usePlaceAdditionalItems()
    return { handleApiError, isSimple, additionalItems }
  },
  data: function () {
    return {
      /**
       * 検索条件
       */
      filterCondition: {
        keyword: '',
        keywordTargets: [],
        placeGroupId: null,
        userIds: [],
        organizationIds: [],
        date: {
          start: null,
          end: null,
        },
      },
      /**
       * プレイス件数
       * @type {Number}
       */
      placeCount: 0,
      /**
       * カードの高さ(px)
       * @type {Number}
       */
      cardHeight: INITIAL_CARD_HEIGHT,
      /**
       * プレイスデータ
       * @type {Array.<Object>}
       */
      places: [],
      /**
       * ページ番号
       * @type {Number}
       */
      currentPage: 1,
      /**
       * 地図表示対象プレイスID
       * @type {Array.<Number>}
       */
      placeIdsForMap: null,
      /**
       * ロード中フラグ
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * 非表示中のbounds変化フラグ
       * @type {Boolean}
       */
      isBoundsChangedWhileUnshown: false,
    }
  },
  computed: {
    /**
     * 地図boundsのBoundsオブジェクト
     * @type {Bounds}
     */
    mapBounds: function () {
      return new Bounds(this.bounds)
    },
    /**
     * 絞り込み条件のフロアID
     * @type {Number}
     */
    indoorLevelId: function () {
      return this.condition.indoor?.indoorLevelId
    },
    /**
     * 検索条件が全く指定されていない場合にtrueを返却する
     * @type {Boolean}
     */
    isSearchParamsEmpty: function () {
      const { keyword, placeGroupId, userIds, organizationIds, date } =
        this.filterCondition
      return (
        placeGroupId == null &&
        userIds.length === 0 &&
        organizationIds.length === 0 &&
        Object.values(date).every((value) => !value) &&
        (keyword == null || keyword === '') // フォームを空にすると空文字を返すことへの対応
      )
    },
    /**
     * 表示範囲内のプレイスのみ表示する場合はtrue
     * - 検索条件が設定されていない
     * - 屋外
     * @type {Boolean}
     */
    toShowPlacesOnlyInBounds: function () {
      return this.isSearchParamsEmpty && !this.indoorLevelId
    },
    /**
     * API用パラメータ
     * @type {Object}
     */
    apiParams: function () {
      const params = {}
      const {
        keyword,
        keywordTargets,
        placeGroupId,
        userIds,
        organizationIds,
        date,
      } = this.filterCondition

      // プレイスグループ
      if (placeGroupId) {
        params.place_group_id = placeGroupId
      }
      // ユーザーID
      if (userIds.length > 0) {
        params.extend_user_id = userIds.join(',')
      }
      // 組織ID
      if (organizationIds.length > 0) {
        params.organization_id = organizationIds.join(',')
      }
      // 検索期間
      const startMoment = toMoment(date.start)
      if (startMoment) {
        params.from = toServerTz(+startMoment.startOf('day'))
      }
      const endMoment = toMoment(date.end)
      if (endMoment) {
        params.to = toServerTz(+endMoment.endOf('day'))
      }
      // キーワード
      if (keyword) {
        // 全角スペースを半角スペースに置き換える
        params.keyword = keyword.replace(/　/g, ' ')
        // 検索項目パラメータ追加
        if (keywordTargets && keywordTargets.length) {
          params['keyword_target'] = keywordTargets.join(',')
        }
      }
      // 屋内レベル
      params.indoor_level_id = this.indoorLevelId || NULL_STRING_API
      // 表示範囲
      if (this.toShowPlacesOnlyInBounds) {
        params.bbox = this.mapBounds.toString()
      }

      return params
    },
    /**
     * watcher監視用の任意項目
     * @type {String}
     */
    additionalItemsForWatch: function () {
      return this.additionalItems.join('|')
    },
    /**
     * watcher監視用APIパラメータ
     * @type {String}
     */
    apiParamsForWatch: function () {
      return JSON.stringify(this.apiParams)
    },
    /**
     * カード用プレイスデータ
     * @type {Array.<Object>}
     */
    placesForCard: function () {
      return this.places.map((place) => {
        const {
          id,
          place_name,
          address1,
          place_attach_files,
          place_groups,
          place_user_relations,
          place_organization_relations,
          position_fix_flg,
          indoor_level_id,
        } = place
        return {
          id,
          placeName: place_name,
          placeAddress: address1,
          items: place.additional_items || [],
          simple: this.isSimple,
          attachFilesCount:
            (place_attach_files && place_attach_files.length) || 0,
          placeGroupNames:
            (place_groups && place_groups.map((g) => g.place_group_name)) || [],
          placeUserRelationNames: [
            ...(place_organization_relations || []).map(
              (o) =>
                `${o.organization_name} (${
                  o.organization_user_relations?.length || 0
                })`
            ),
            ...(place_user_relations || []).map((u) => u.user_name),
          ],
          isFixedPosition: position_fix_flg,
          indoorLevelId: indoor_level_id,
        }
      })
    },
  },
  watch: {
    show: function (value) {
      if (value) {
        // 非表示の間にboundsが変化していたら更新する
        if (this.toShowPlacesOnlyInBounds && this.isBoundsChangedWhileUnshown) {
          this.$_showPlaces()
        }
        // bounds変更フラグ初期化
        this.isBoundsChangedWhileUnshown = false
        // 非表示の間は高さを計測できないので、表示に切り替わったときに計測する
        if (this.placesForCard.length) {
          this.$nextTick().then(() => {
            this.$_getCardHeight()
          })
        }
      }
    },
    currentPage: function () {
      this.$_showPlaces()
    },
    additionalItemsForWatch: function () {
      this.$_showPlaces()
    },
    // カードの高さが変わり得るデータに変化があったら仮想スクロール用にカード高さを再取得
    placesForCard: {
      immediate: true,
      handler: function (value) {
        if (this.show && value.length) {
          this.$nextTick().then(() => {
            this.$_getCardHeight()
          })
        }
      },
    },
    apiParamsForWatch: function () {
      if (this.toShowPlacesOnlyInBounds) {
        this.$_showPlacesDebounce()
      } else {
        this.$_showPlaces()
      }
    },
    bounds: function () {
      if (!this.show) {
        // フラグ更新
        this.isBoundsChangedWhileUnshown = true
      }
    },
    placeIdsForMap: function (value) {
      /**
       * 地図表示対象ID変更時イベント
       * @param {Object} targetIds 表示対象ID
       */
      this.$emit('target-ids-changed', { place: value })
    },
  },
  created: function () {
    this.$_showPlacesDebounce = _.debounce(this.$_showPlaces, 250)
  },
  mounted: function () {
    this.$log.debug('AppMemberPlace mounted')
  },
  methods: {
    /**
     * 表示更新
     * @public
     */
    refresh: function () {
      // 表示時のみ再更新する(非表示時は表示された時に更新が走るので不要)
      if (this.show) {
        this.$_showPlaces()
      }
    },

    /**
     * プレイスデータを表示
     */
    $_showPlaces: async function () {
      // API実行中にpropsが変化する可能性があるので実行前に値を保持しておく
      const canFitMapToAlone = this.canFitMapToAlone
      this.$_showLoading()

      // プレイス取得
      const results = await Promise.allSettled([
        this.$_getPlaces(),
        this.$_getPlaceCount(),
        this.$_updatePlaceIdsForMap(),
      ])
      const failedResults = results.filter((r) => r.status === 'rejected')
      if (failedResults.length) {
        failedResults.forEach((r) => this.$log.error(r.reason))
        this.$_hideLoading()
        return
      }

      // 地図範囲に依らない取得でプレイス総件数が1件なら地図をfitする
      if (
        !this.toShowPlacesOnlyInBounds &&
        this.placeCount === 1 &&
        canFitMapToAlone
      ) {
        // 擬似的にカードをクリックしたことにする
        this.$_onClickCardBody(null, this.places[0].id)
      }

      // 一覧のスクロールバーを先頭に戻す
      this.$_moveScrollToTop()

      this.$_hideLoading()
    },

    /**
     * プレイスデータ取得
     */
    $_getPlaces: async function () {
      const url = RestApiClient.createApiUrl('place')
      const params = url.searchParams
      this.$_addSearchCondition(params)

      // 任意項目（件数取得やID取得には付けないので、ここで付ける）
      if (this.additionalItems.length) {
        params.append('additional_items', this.additionalItems.join('|'))
      }

      // limit, offset
      params.append('limit', this.limit)
      const offset = (this.currentPage - 1) * this.limit
      params.append('offset', offset)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.places = json.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * プレイス件数取得
     */
    $_getPlaceCount: async function () {
      const url = RestApiClient.createApiUrl('place/count')
      const params = url.searchParams
      this.$_addSearchCondition(params)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.placeCount = json.data.count
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * 地図上に表示するプレイスIDセットを更新する
     */
    $_updatePlaceIdsForMap: async function () {
      if (this.toShowPlacesOnlyInBounds) {
        // IDでのfilteringは必要ない
        this.placeIdsForMap = null
        return
      }
      await this.$_getPlaceIdsForMap()
    },

    /**
     * 対象プレイスID取得
     */
    $_getPlaceIdsForMap: async function () {
      const url = RestApiClient.createApiUrl('place/id')
      const params = url.searchParams
      this.$_addSearchCondition(params)

      try {
        const json = await RestApiClient.executeGetApi(url)
        this.placeIdsForMap = json.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * URLSearchParamsに検索パラメータを追加する(limit, offsetは含まない)
     * @param {URLSearchParams} params パラメータを追加するURLSearchParams
     */
    $_addSearchCondition: function (params) {
      for (const [key, value] of Object.entries(this.apiParams)) {
        params.append(key, value)
      }
    },

    /**
     * スクロールバーをトップに戻す
     */
    $_moveScrollToTop: function () {
      this.$refs['place-list-inner']?.scrollToPosition(0)
    },

    /**
     * プレイスカードをクリックした時の処理
     * @param {Number} timelineType タイムラインタイプ(使用しない)
     * @param {Number} recordId クリックしたカードのレコードID
     */
    $_onClickCardBody: function (timelineType, recordId) {
      // 地図のフォーカスを選択したデータに設定する
      const place = this.places.find((place) => place.id === recordId)
      if (place) {
        /**
         * アイテムクリックイベント
         * @param {Object} place プレイスデータ
         */
        this.$emit('item-click', place)
      }
    },

    /**
     * プレイスカードのタイトルをクリックした時の処理
     * @param {Number} timelineType タイムラインタイプ(使用しない)
     * @param {Number} recordId クリックしたカードのレコードID
     */
    $_onClickCardTitle: function (timelineType, recordId) {
      /**
       * 名称クリックイベント
       * @param {Number} id 対象プレイスID
       */
      this.$emit('name-click', recordId)
    },

    /**
     * カードの高さを取得する
     */
    $_getCardHeight: function () {
      const rect = this.$refs['card']?.$el.getBoundingClientRect()
      if (rect?.height) {
        this.cardHeight = rect.height
        this.$log.debug('[DEBUG] height of place card:', this.cardHeight)
      }
    },

    /**
     * 表示項目を変更した時の処理
     * @param {Array.<String>} items 選択した表示項目
     */
    $_onItemsChanged: function (items) {
      this.additionalItems = items
    },

    /**
     * 簡易表示を切り替えた時の処理
     * @param {Boolean} value 簡易表示するならtrue
     */
    $_onIsSimpleChanged: function (value) {
      this.isSimple = value
    },

    /**
     * 報告書ページへ遷移する
     * @param {Array.<Number>} placeIds プレイスID配列
     */
    $_showReport: function (placeIds) {
      this.$router.push({
        name: 'report',
        params: {
          defaultPlaceIds: placeIds,
        },
      })
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/tool-bar';
@use '../../assets/scss/parts/title-bar';

.member-place {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
}

.tool-bar {
  height: auto;
  border-top-right-radius: 8px;
  background-color: #ffffff;
  overflow-y: hidden;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 8px;
}

.list-wrapper {
  overflow: hidden;

  .loading-mask {
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
  }
}

.list-inner {
  height: auto;
  overflow-y: auto;
  @include mixin-scrollbar;

  ::v-deep .resize-observer {
    display: none;
  }
}

// カードの外側の余白と区切り線。余白と線の色は Figma（プレイスのヘッダー表示について_20260813）、
// ホバーと右の線はユーザーのカード（AppMemberUser.vue の .user-item）に合わせている
.place-item {
  padding: 12px 10px;
  border-bottom: 1px solid #eaecef;
  border-right: 1px solid #eaecef;
  cursor: pointer;

  &:hover {
    background-color: #f9fafc;
  }
}

.title-bar .selector {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.hidden {
  z-index: -1;
  opacity: 0;
  visibility: hidden;
  position: fixed;
  top: -999px;
  left: -999px;
  pointer-events: none;
}

.list-footer {
  width: 100%;
  padding: 8px 0 6px;
  border-top: $list-footer-border-width solid #eaecef;
}

.place-pagination {
  @include mixin-pagination;
}
</style>
